import { useCallback, useMemo } from "react";
import { useApolloClient } from "@apollo/client";
import { noop } from "lodash";

import { useAppDispatch, useAppSelector } from "../../../Store";

import Graph_QL, { DynamicDataObk, GqlHook_Returns, StateDataObject } from "..";
import { gqlActions } from "../gqlData_Reducer";
import { filterByConnection } from "../filterByConnection";

import { PanelBasicQueryInfoInput } from "./GQLCalls/getFeedComponentsCall";
import { FeedCurveItem } from "./FeedDataSlices/feedCurveSlice";
import { CREATE_FEED_CURVE, CreateFeedCurveResponse } from "./GQLCalls/createFeedCurveCall";
import { GET_FEED_CURVES, FeedCurvesResponse, FeedCurve } from "./GQLCalls/getFeedCurvesCall";
import { MAINTAIN_FEED_CURVE, MaintainFeedCurveCreates, MaintainFeedCurveDelete, MaintainFeedCurveResponse, MaintainFeedCurveUpdates, MaintainFeedCurveVariables } from "./GQLCalls/maintainFeedCurveCall";

interface maintainFeedCurveElements {
  createFeedCurve: (name: String, specific?: Graph_QL.GQLCons) => Promise<{success: boolean, feedcurve_id: number}>, 
}

export interface FeedCurveDirectives {
  ifFeedCurveBaseInfoOnly?: boolean,
  ifFeedCurveInfo?: boolean,
  ifSpreading?: boolean,
}

export const useFeedCurves = (connection: Graph_QL.Connection): GqlHook_Returns<FeedCurveItem, PanelBasicQueryInfoInput & FeedCurveDirectives> & maintainFeedCurveElements => {
  const dispatch = useAppDispatch();
  const client = useApolloClient();

  const feedCurvesState = useAppSelector(({graphQlData:{domain}}) => domain.feedCurves);
  const {fullState} = useMemo(() => {
    const state = filterByConnection(feedCurvesState, Graph_QL.ToArr(connection)) as Record<string, FeedCurveItem>
    return Object.entries(state).reduce((accumulator, [key, data]) => {
      if (data.data) {
        const value = {...data.data, domain:key}; 
        accumulator.data = accumulator.data.concat(value)
      }
      return accumulator
    },{fullState: state, data: []} as StateDataObject<FeedCurveItem>)
  },[connection, feedCurvesState]);

  const getFeedCurves = useCallback(async (input: PanelBasicQueryInfoInput & FeedCurveDirectives, specific?: Graph_QL.GQLCons,  ) => {
    const {ifFeedCurveBaseInfoOnly, ifFeedCurveInfo, ifSpreading, ...remainingInput} = input
    const usedConnection = specific?.connection ? specific.connection : connection.connection 
    try {
      client.query<FeedCurvesResponse>({query: GET_FEED_CURVES, variables: {...usedConnection, input: remainingInput, ifFeedCurveBaseInfoOnly: ifFeedCurveBaseInfoOnly ?? false, ifFeedCurveInfo: ifFeedCurveInfo ?? false, ifSpreading: ifSpreading ?? false}, fetchPolicy: input.ids && input.ids?.length > 0 ? "network-only" : undefined})
      .then((e) => {
        if (e && e.data) {
          dispatch({type: String(gqlActions.domain.feedCurves.maintained), payload: {con: usedConnection, data: e.data.feedCurve.info}});
        }
      }).catch((error) => {console.log(error);})
    } catch (error) {
      console.log(error);
      // TODO CTO: clean up when everything is stable
    }
  },[client, connection.connection, dispatch])
 
  //   useEffect(() => {
  //   if (!(fullState[Graph_QL.ObjToString(connection.connection)]?.data?.length < 50)) {
  //     getFeedCurves({fetchAmount: 50, ifBaseInfoOnly: true})
  //   }
  // },[connection.connection, fullState, getFeedCurves])
  
  const fetchMore = async (cursor: number|number[], specific?: Graph_QL.GQLCons) => {
    
    }

  const maintainFeedCurve =  useCallback(async (changed?: Partial<DynamicDataObk<MaintainFeedCurveUpdates>>, deleted?: Partial<DynamicDataObk<MaintainFeedCurveDelete>>, created?: Partial<DynamicDataObk<MaintainFeedCurveCreates>>, feedCurve?: {originalFeedCurve: FeedCurve, optimisticFeedCurve: FeedCurve}) => {
    if (feedCurve === undefined) {
      return;
    }
    const maintainFeedCurve = {creates: created?.data, deletes: deleted?.data, updates: changed?.data, feedCurveId: feedCurve.originalFeedCurve.feedCurveId}
    try {
      dispatch({type: String(gqlActions.domain.feedCurves.maintained), payload: {con: connection.connection, data: [feedCurve.optimisticFeedCurve]}})
      client.mutate<MaintainFeedCurveResponse, MaintainFeedCurveVariables>({mutation: MAINTAIN_FEED_CURVE, variables: {...connection.connection, maintainFeedCurve}})
        .then((response) => {
          if (response && response.data) {
            if (response.data.feedCurve.maintain < 0) {
              // TODO consider alert that something went wrong when saving
              dispatch({type: String(gqlActions.domain.feedCurves.maintained), payload: {con: connection.connection, data: [feedCurve.originalFeedCurve]}})
            return
            }
            getFeedCurves({ids: [response.data.feedCurve.maintain], ifSpreading: true})
          }
        }).catch((error) => {console.log(error)})
    } catch (error) {
      console.log(error)
    }
  },[client, connection.connection, dispatch, getFeedCurves])

  const createFeedCurve = useCallback(async (name: String, specific?: Graph_QL.GQLCons) => {
    const usedConnection = specific ? specific.connection : connection.connection 
    try {
      const response = await client.mutate<CreateFeedCurveResponse>({mutation: CREATE_FEED_CURVE, variables: {...usedConnection, name}})
      if (response) {
        const createdFeedCurve = response.data?.createFeedCurve;
        if (createdFeedCurve && createdFeedCurve.success && createdFeedCurve.updatedValues.length > 0) {
          const createdFeedCurveInfo = createdFeedCurve.updatedValues[0];
          dispatch({type: String(gqlActions.domain.feedCurves.maintained), payload: {con: usedConnection, data: [{name, feedCurveId: createdFeedCurveInfo.feedcurve_id, milestones: []}]}})
          getFeedCurves({ids: [createdFeedCurveInfo.feedcurve_id], ifSpreading: true});
          return {success: true, feedcurve_id: createdFeedCurveInfo.feedcurve_id}
        }
      }
  
    return {success: false, feedcurve_id: -1};
    } catch (error) {
      return {success: false, feedcurve_id: -1};
    }
  },[client, connection.connection, dispatch, getFeedCurves])

  return {call: getFeedCurves, fetchMore, deleteEntries: noop, data: fullState, maintainEntries: maintainFeedCurve, createFeedCurve}
} 

